import type {
    ComponentChildren,
    JSX,
} from "preact";
import {
    forwardRef,
} from "preact/compat";

type IconButtonSize = "sm" | "md" | "lg";
type IconButtonVariant = "ghost" | "subtle";

type IconButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
    children: ComponentChildren;
    size?: IconButtonSize;
    variant?: IconButtonVariant;
    "aria-label": string;
};

const sizeStyles: Record<IconButtonSize, string> = {
    sm: "p-1",
    md: "p-2",
    lg: "p-3",
};

const variantStyles: Record<IconButtonVariant, string> = {
    ghost: "text-text-secondary hover:text-text-primary hover:bg-bg-tertiary",
    subtle: "text-text-secondary hover:text-accent hover:bg-accent/10",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({
        children,
        size = "md",
        variant = "ghost",
        className = "",
        disabled,
        ...props
    }, ref) => {
        return (
            <button
                ref={ref}
                type="button"
                disabled={disabled}
                className={`
                    inline-flex items-center justify-center rounded-lg transition-colors
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${sizeStyles[size]}
                    ${variantStyles[variant]}
                    ${className}
                `.trim().replace(/\s+/g, " ")}
                {...props}
            >
                {children}
            </button>
        );
    },
);
